import { useEffect, useState } from 'react';
import { cx } from '@/lib/cx';
import { useT } from '@/i18n/LocaleProvider';
import type { UiKey } from '@/i18n/dictionary';
import styles from './Catalog.module.css';
import type { CardSize, CatalogLayout } from './catalogPrefs';

interface Props {
  layout: CatalogLayout;
  size: CardSize;
  onLayout: (layout: CatalogLayout) => void;
  onSize: (size: CardSize) => void;
}

const KNOB_LEFT: Record<CardSize, string> = {
  s: 'calc(16.66% - 7px)',
  m: 'calc(50% - 7px)',
  l: 'calc(83.33% - 7px)',
};
const SIZE_TITLES: Record<CardSize, UiKey> = {
  s: 'catalog.sizeS',
  m: 'catalog.sizeM',
  l: 'catalog.sizeL',
};

/** Тихая кнопка «View»: раскладка Grid / Natural и размер карточек S·M·L (CAT-7). */
export function ViewPopover({ layout, size, onLayout, onSize }: Props) {
  const [open, setOpen] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={styles.viewWrap}>
      <button
        type="button"
        title={t('catalog.viewOptions')}
        aria-label={t('catalog.viewOptions')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cx(styles.viewButton, open && styles.viewButtonOpen)}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.viewIcon} aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </span>
      </button>
      {open ? (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.viewPopover} role="dialog" aria-label="View options">
            <div>
              <p className={styles.popEyebrow}>{t('catalog.layout')}</p>
              <div className={styles.segment} role="radiogroup" aria-label={t('catalog.layout')}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={layout === 'grid'}
                  className={cx(styles.segmentButton, layout === 'grid' && styles.segmentActive)}
                  onClick={() => onLayout('grid')}
                >
                  {t('catalog.layoutGrid')}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={layout === 'natural'}
                  className={cx(styles.segmentButton, layout === 'natural' && styles.segmentActive)}
                  onClick={() => onLayout('natural')}
                >
                  {t('catalog.layoutNatural')}
                </button>
              </div>
            </div>
            <div>
              <p className={styles.popEyebrow}>{t('catalog.cardSize')}</p>
              <div className={styles.sizeRow}>
                <span className={styles.sizeLabel}>S</span>
                <div
                  className={styles.sizeTrack}
                  role="radiogroup"
                  aria-label={t('catalog.cardSize')}
                >
                  <span className={styles.sizeLine} aria-hidden="true" />
                  {(['s', 'm', 'l'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={size === value}
                      title={t(SIZE_TITLES[value])}
                      className={styles.sizeStop}
                      onClick={() => onSize(value)}
                    >
                      <span />
                    </button>
                  ))}
                  <span
                    className={styles.sizeKnob}
                    style={{ left: KNOB_LEFT[size] }}
                    aria-hidden="true"
                  />
                </div>
                <span className={styles.sizeLabel}>L</span>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
